import type { ChartFact, ChartResult, GocharPlacement, TransitHit } from '@astrally/shared';
import { formatDegMin } from '../math/angles.js';
import { activePeriods } from '../jyotish/vimshottari.js';

/**
 * Builds the structured fact pack that grounds Ally. Ally may only reference
 * these facts — it never computes chart values itself. Fact ids are stable so
 * the UI can render tappable citations.
 */

export function buildChartFacts(chart: ChartResult): ChartFact[] {
  const facts: ChartFact[] = [];

  facts.push({
    id: 'meta.timeConfidence',
    label: 'Birth time status',
    value:
      chart.meta.timeConfidence === 'exact'
        ? 'Exact birth time'
        : chart.meta.timeConfidence === 'approximate'
          ? 'Approximate birth-time window'
          : 'Unknown birth time (full-day analysis)',
    system: 'meta',
    confidence: 'exact',
  });

  if (chart.positions) {
    for (const p of chart.positions) {
      facts.push({
        id: `jyotish.${p.planet}.position`,
        label: `${cap(p.planet)} (sidereal)`,
        value: `${p.sidereal.signName} ${formatDegMin(p.sidereal.degInSign)}, ${p.nakshatra.name} pada ${p.nakshatra.pada}${p.retrograde ? ', retrograde' : ''}`,
        system: 'jyotish',
        confidence: 'exact',
      });
      facts.push({
        id: `western.${p.planet}.position`,
        label: `${cap(p.planet)} (tropical)`,
        value: `${p.tropical.signName} ${formatDegMin(p.tropical.degInSign)}${p.retrograde ? ', retrograde' : ''}`,
        system: 'western',
        confidence: 'exact',
      });
    }
  }

  if (chart.jyotish?.lagna) {
    facts.push({
      id: 'jyotish.lagna',
      label: 'Lagna (ascendant)',
      value: `${chart.jyotish.lagna.signName} ${formatDegMin(chart.jyotish.lagna.degInSign)}`,
      system: 'jyotish',
      confidence: 'exact',
    });
  }

  if (chart.western?.angles) {
    facts.push({
      id: 'western.ascendant',
      label: 'Ascendant',
      value: describeLon(chart.western.angles.ascendant),
      system: 'western',
      confidence: 'exact',
    });
    facts.push({
      id: 'western.mc',
      label: 'Midheaven',
      value: describeLon(chart.western.angles.mc),
      system: 'western',
      confidence: 'exact',
    });
  }

  if (chart.jyotish?.vimshottari) {
    const chain = activePeriods(chart.jyotish.vimshottari, new Date());
    if (chain.length > 0) {
      facts.push({
        id: 'jyotish.dasha.current',
        label: 'Current dasha',
        value: chain
          .map((p) => `${cap(p.lord)} ${['maha', 'antar', 'pratyantar'][p.level - 1]}dasha`)
          .join(' → '),
        system: 'jyotish',
        confidence: chart.meta.timeConfidence === 'exact' ? 'exact' : 'range',
      });
      const maha = chain[0];
      facts.push({
        id: 'jyotish.dasha.mahadashaSpan',
        label: `${cap(maha.lord)} mahadasha period`,
        value: `${maha.startISO.slice(0, 10)} to ${maha.endISO.slice(0, 10)}`,
        system: 'jyotish',
        confidence: chart.meta.timeConfidence === 'exact' ? 'exact' : 'range',
      });
    }
  }

  if (chart.western) {
    for (const a of chart.western.aspects.slice(0, 20)) {
      facts.push({
        id: `western.aspect.${a.a}-${a.type}-${a.b}`,
        label: 'Aspect',
        value: `${cap(String(a.a))} ${a.type} ${cap(String(a.b))} (orb ${a.orb.toFixed(1)}°)`,
        system: 'western',
        confidence: 'exact',
      });
    }
    for (const p of chart.western.patterns) {
      facts.push({
        id: `western.pattern.${p.type}-${p.points.join('-')}`,
        label: 'Chart pattern',
        value: `${p.type}: ${p.detail}`,
        system: 'western',
        confidence: 'exact',
      });
    }
  }

  if (chart.jyotish) {
    for (const y of chart.jyotish.yogas.filter((y) => y.present)) {
      facts.push({
        id: `jyotish.yoga.${y.id}`,
        label: y.name,
        value: y.factors.join('; '),
        system: 'jyotish',
        confidence: chart.meta.timeConfidence === 'exact' ? 'exact' : 'range',
      });
    }
  }

  // Window modes: stable facts are certain; varying become ranges; unavailable stated.
  if (chart.uncertainty) {
    for (const f of chart.uncertainty.stable) {
      facts.push({
        id: `stable.${f.id}`,
        label: f.label,
        value: f.value,
        system: f.system === 'both' ? 'meta' : f.system,
        confidence: 'exact',
      });
    }
    for (const f of chart.uncertainty.varying) {
      facts.push({
        id: `varying.${f.id}`,
        label: f.label,
        value: f.possibilities
          .map((p) => `${p.value} (${p.fromLocal}–${p.toLocal})`)
          .join(' OR '),
        system: f.system === 'both' ? 'meta' : f.system,
        confidence: 'range',
      });
    }
    for (const f of chart.uncertainty.unavailable) {
      facts.push({
        id: `unavailable.${f.id}`,
        label: f.label,
        value: `Not calculable: ${f.reason}`,
        system: 'meta',
        confidence: 'unknown',
      });
    }
    if (chart.uncertainty.dasha) {
      facts.push({
        id: 'jyotish.dasha.possibilities',
        label: 'Possible birth mahadashas',
        value: chart.uncertainty.dasha.possibilities
          .map((d) => `${cap(d.mahadashaLord)} (from ${d.nakshatraName}, ${(d.fraction * 100).toFixed(0)}% of window)`)
          .join('; '),
        system: 'jyotish',
        confidence: 'range',
      });
    }
  }

  if (chart.reference) {
    facts.push({
      id: 'meta.reference',
      label: 'Reference chart',
      value: chart.reference.note,
      system: 'meta',
      confidence: 'unknown',
    });
  }

  return facts;
}

export function buildTransitFacts(gochar: GocharPlacement[], transits: TransitHit[]): ChartFact[] {
  const facts: ChartFact[] = [];
  for (const g of gochar) {
    facts.push({
      id: `gochar.${g.planet}`,
      label: `${cap(g.planet)} now (gochar)`,
      value: `${g.sidereal.signName} ${formatDegMin(g.sidereal.degInSign)}, ${g.nakshatra.name}${g.houseFromMoon ? `, house ${g.houseFromMoon} from your Moon` : ''}${g.retrograde ? ', retrograde' : ''}`,
      system: 'jyotish',
      confidence: 'exact',
    });
  }
  for (const t of transits.slice(0, 15)) {
    facts.push({
      id: `transit.${t.transiting}-${t.type}-${t.natal}`,
      label: 'Current transit',
      value: `Transiting ${cap(t.transiting)} ${t.type} natal ${cap(String(t.natal))} (orb ${t.orb.toFixed(1)}°)`,
      system: 'western',
      confidence: 'exact',
    });
  }
  return facts;
}

function cap(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function describeLon(lon: number): string {
  const signNames = ['Aries', 'Taurus', 'Gemini', 'Cancer', 'Leo', 'Virgo', 'Libra', 'Scorpio', 'Sagittarius', 'Capricorn', 'Aquarius', 'Pisces'];
  const sign = Math.floor(((lon % 360) + 360) % 360 / 30);
  const deg = ((lon % 360) + 360) % 360 - sign * 30;
  return `${signNames[sign]} ${formatDegMin(deg)}`;
}
